import { x25519 } from "@noble/curves/ed25519.js";
import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { assertKeyLength, bytesToUtf8, concatBytes, generateNonce, utf8ToBytes, zeroBytes } from "./bytes.js";
import { CryptoError } from "./errors.js";
/**
 * Extension link (docs/crypto-spec.md section 14): a signed-in web app hands the account keys and
 * a fresh session to a browser extension (or desktop app) that holds a one-time X25519 key pair.
 * The server only relays the sealed blob.
 */
const LINK_LABEL = utf8ToBytes("passwordflow/extension-link-v1");
const CODE_LABEL = utf8ToBytes("passwordflow/link-code-v1");
const POLL_LABEL = utf8ToBytes("passwordflow/link-poll-v1");
const SEPARATOR = new Uint8Array([0]);
export const LINK_REQUEST_ID_BYTES = 16;
export const LINK_POLL_SECRET_BYTES = 32;
const PUBLIC_KEY_BYTES = 32;
const NONCE_BYTES = 24;
const TAG_BYTES = 16;
const CODE_MODULUS = 1_000_000;
/** Upper bound for the canonical plaintext; the sealed blob adds 72 bytes. */
export const LINK_PAYLOAD_MAX_BYTES = 2048;
/** Any clamped scalar sends every low-order point to zero, which noble refuses. */
const PROBE_SCALAR = new Uint8Array(32).fill(0x5a);
function joinAll(parts) {
    return parts.reduce((acc, part) => concatBytes(acc, part), new Uint8Array(0));
}
function assertRequestId(requestId) {
    if (requestId.length !== LINK_REQUEST_ID_BYTES) {
        throw new CryptoError("invalid-input", "Link request id must be 16 bytes.");
    }
}
/** A usable X25519 public key: 32 bytes and not a low-order point. */
export function isValidLinkPublicKey(publicKey) {
    if (publicKey.length !== PUBLIC_KEY_BYTES) {
        return false;
    }
    try {
        zeroBytes(x25519.getSharedSecret(PROBE_SCALAR, publicKey));
        return true;
    }
    catch {
        return false;
    }
}
/**
 * The six-digit code both sides show: binds the request id and the extension's public key.
 * Returned without spaces, zero-padded; UIs display it as "123 456".
 */
export function linkCode(requestId, publicKey) {
    assertRequestId(requestId);
    assertKeyLength(publicKey, PUBLIC_KEY_BYTES, "LinkPublicKey");
    const digest = sha256(joinAll([CODE_LABEL, SEPARATOR, requestId, publicKey]));
    const value = new DataView(digest.buffer, digest.byteOffset, 4).getUint32(0, false) & 0x7fffffff;
    return String(value % CODE_MODULUS).padStart(6, "0");
}
/** "123456" → "123 456". */
export function formatLinkCode(code) {
    return `${code.slice(0, 3)} ${code.slice(3)}`;
}
/** What the server stores instead of the poll secret. */
export function linkPollHash(pollSecret) {
    assertKeyLength(pollSecret, LINK_POLL_SECRET_BYTES, "LinkPollSecret");
    return sha256(concatBytes(POLL_LABEL, pollSecret));
}
function linkInfo(requestId) {
    return joinAll([LINK_LABEL, SEPARATOR, requestId]);
}
function linkAad(requestId, webPub, extensionPub) {
    return joinAll([LINK_LABEL, SEPARATOR, requestId, webPub, extensionPub]);
}
function deriveLinkKey(shared, requestId, webPub, extensionPub) {
    return hkdf(sha256, shared, concatBytes(webPub, extensionPub), linkInfo(requestId), 32);
}
function sharedSecret(secretKey, publicKey) {
    try {
        return x25519.getSharedSecret(secretKey, publicKey);
    }
    catch {
        throw new CryptoError("invalid-input", "Link public key is not usable.");
    }
}
/** Seals the link payload to the extension's one-time key. Output: webPub(32) | nonce(24) | ciphertext. */
export function sealLinkPayload(requestId, extensionPub, plaintext) {
    const ephemeral = x25519.keygen();
    try {
        return sealLinkPayloadWith(requestId, extensionPub, plaintext, ephemeral.secretKey, generateNonce());
    }
    finally {
        zeroBytes(ephemeral.secretKey);
    }
}
/** Fixed ephemeral key and nonce, for the published test vectors only. */
export function sealLinkPayloadWith(requestId, extensionPub, plaintext, ephemeralSecret, nonce) {
    assertRequestId(requestId);
    assertKeyLength(extensionPub, PUBLIC_KEY_BYTES, "LinkPublicKey");
    assertKeyLength(ephemeralSecret, 32, "EphemeralSecretKey");
    if (nonce.length !== NONCE_BYTES) {
        throw new CryptoError("invalid-nonce-length", "Nonce must be 24 bytes.");
    }
    if (plaintext.length > LINK_PAYLOAD_MAX_BYTES) {
        throw new CryptoError("invalid-input", "Link payload is too large.");
    }
    const webPub = x25519.getPublicKey(ephemeralSecret);
    const shared = sharedSecret(ephemeralSecret, extensionPub);
    const key = deriveLinkKey(shared, requestId, webPub, extensionPub);
    try {
        const ciphertext = xchacha20poly1305(key, nonce, linkAad(requestId, webPub, extensionPub)).encrypt(plaintext);
        return joinAll([webPub, nonce, ciphertext]);
    }
    finally {
        zeroBytes(shared);
        zeroBytes(key);
    }
}
/** Opens a link blob with the extension's one-time key pair. Wrong id, key or any change: decrypt-failed. */
export function openLinkPayload(requestId, identity, sealed) {
    assertRequestId(requestId);
    if (sealed.length < PUBLIC_KEY_BYTES + NONCE_BYTES + TAG_BYTES) {
        throw new CryptoError("invalid-input", "Sealed payload is too short.");
    }
    const webPub = sealed.slice(0, PUBLIC_KEY_BYTES);
    const nonce = sealed.slice(PUBLIC_KEY_BYTES, PUBLIC_KEY_BYTES + NONCE_BYTES);
    const ciphertext = sealed.slice(PUBLIC_KEY_BYTES + NONCE_BYTES);
    try {
        const shared = x25519.getSharedSecret(identity.secretKey, webPub);
        const key = deriveLinkKey(shared, requestId, webPub, identity.publicKey);
        try {
            return xchacha20poly1305(key, nonce, linkAad(requestId, webPub, identity.publicKey)).decrypt(ciphertext);
        }
        finally {
            zeroBytes(shared);
            zeroBytes(key);
        }
    }
    catch {
        throw new CryptoError("decrypt-failed", "Decryption failed.");
    }
}
const B64 = /^[A-Za-z0-9+/]+={0,2}$/;
const KEY_B64 = /^[A-Za-z0-9+/]{43}=$/;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function stringOf(record, key) {
    const value = record[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
/** Canonical JSON: fixed key order, no whitespace. */
export function serializeLinkPayload(payload) {
    const bytes = utf8ToBytes(JSON.stringify({
        v: 1,
        serverUrl: payload.serverUrl,
        userId: payload.userId,
        email: payload.email,
        token: payload.token,
        expiresAt: payload.expiresAt,
        vaultKeyB64: payload.vaultKeyB64,
        wrapKeyB64: payload.wrapKeyB64,
        saltB64: payload.saltB64
    }));
    if (bytes.length > LINK_PAYLOAD_MAX_BYTES) {
        throw new CryptoError("invalid-input", "Link payload is too large.");
    }
    return bytes;
}
/** Undefined unless the bytes are a complete version-1 payload with 32-byte keys. */
export function parseLinkPayload(bytes) {
    if (bytes.length > LINK_PAYLOAD_MAX_BYTES) {
        return undefined;
    }
    let raw;
    try {
        raw = JSON.parse(bytesToUtf8(bytes));
    }
    catch {
        return undefined;
    }
    if (!isRecord(raw) || raw["v"] !== 1) {
        return undefined;
    }
    const expiresAt = raw["expiresAt"];
    const serverUrl = stringOf(raw, "serverUrl");
    const userId = stringOf(raw, "userId");
    const email = stringOf(raw, "email");
    const token = stringOf(raw, "token");
    const vaultKeyB64 = stringOf(raw, "vaultKeyB64");
    const wrapKeyB64 = stringOf(raw, "wrapKeyB64");
    const saltB64 = stringOf(raw, "saltB64");
    if (typeof expiresAt !== "number" || !Number.isSafeInteger(expiresAt) || serverUrl === undefined || userId === undefined ||
        email === undefined || token === undefined || vaultKeyB64 === undefined || wrapKeyB64 === undefined || saltB64 === undefined) {
        return undefined;
    }
    if (!KEY_B64.test(vaultKeyB64) || !KEY_B64.test(wrapKeyB64) || !B64.test(saltB64)) {
        return undefined;
    }
    return { v: 1, serverUrl, userId, email, token, expiresAt, vaultKeyB64, wrapKeyB64, saltB64 };
}
//# sourceMappingURL=link.js.map